/**
 * Ordene på Rettigheter (30.09, `docs/qa/rettigheter/UTKAST.md`): seksjonene, innstillingene og de
 * faste reglene, alltid med navn og aldri med koden `resource:verb`.
 *
 * Nøklene bygges av id-er som er data i `@tvitvi/shared` (`PERMISSION_SECTIONS`) og av ressurs og verb
 * i en handling, så prefiksene står i `DYNAMIC_KEY_PREFIXES`. Id-ene har ingen kolon — derfor trengs
 * ikke `nsSeparator: false` her, i motsetning til de gamle `question.<action>`-nøklene.
 */
import {
  CORE,
  PERMISSION_SECTIONS,
  RESOURCE_TYPES,
  modulesOfResource,
  splitAction,
  type Action,
  type OrganizationModule,
  type PermissionModule,
  type ResourceType,
} from '@tvitvi/shared';
import type { TFunction } from 'i18next';

import { dynamicKey } from '@/i18n/types';

export function sectionTitle(t: TFunction, id: PermissionModule): string {
  return t(dynamicKey(`settings.permissions.section.${id}.title`));
}

export function sectionDescription(t: TFunction, id: PermissionModule): string {
  return t(dynamicKey(`settings.permissions.section.${id}.description`));
}

export function settingLabel(t: TFunction, id: string): string {
  return t(dynamicKey(`settings.permissions.setting.${id}.label`));
}

export function settingDescription(t: TFunction, id: string): string {
  return t(dynamicKey(`settings.permissions.setting.${id}.description`));
}

/**
 * Hvilket område en fast regel hører til — gruppene i «Vis det Tvitvi alltid bestemmer».
 *
 * Et oppslag fra ressursnavnet, fordi de faste reglene ikke har noen innstilling å hente modulen fra.
 * En ressurs som ikke står her havner under «Annet», med ressurs og verb i klartekst: en ny handling
 * blir aldri usynlig, den blir bare dårlig sortert til noen legger den inn.
 */
export const FIXED_AREAS = [
  'organization',
  'contacts',
  'tasks',
  'inbox',
  'documents',
  'calendar',
  'structure',
  'festival',
  'people',
  'resources',
  'money',
  'booking',
  'artists',
  'venue',
  'venues',
  'concerts',
  'sharing',
  'activity',
  'other',
] as const;
export type FixedArea = (typeof FIXED_AREAS)[number];

const AREA_OF_RESOURCE: Readonly<Record<string, FixedArea>> = {
  organization: 'organization',
  membership: 'organization',
  member_profile: 'organization',
  superadmin_profile: 'organization',
  organization_partner: 'organization',
  invitation: 'organization',
  user: 'organization',
  person: 'organization',
  role_permission: 'organization',
  consent: 'organization',
  notification: 'organization',
  contact: 'contacts',
  task: 'tasks',
  thread: 'inbox',
  message: 'inbox',
  document: 'documents',
  folder: 'documents',
  event: 'calendar',
  department: 'structure',
  tag: 'structure',
  person_field: 'structure',
  participation_role: 'structure',
  festival: 'festival',
  edition: 'festival',
  performance: 'festival',
  stage: 'festival',
  participation: 'people',
  crew_member: 'people',
  shift: 'people',
  volunteer: 'people',
  signup_form: 'people',
  accreditation: 'people',
  accreditation_level: 'people',
  resource_type: 'resources',
  resource: 'resources',
  allocation: 'resources',
  economy: 'money',
  contract: 'money',
  /* «Avtaler og provisjon» er byråets (253.61), ikke Økonomi: gruppert etter ressursens modul (G1). */
  artist_agreement: 'artists',
  agency_move: 'booking',
  artist: 'artists',
  /* G2 (0923): Booking og Artister har egne ressurser. */
  booking: 'booking',
  booking_artist: 'booking',
  booking_draft: 'booking',
  booking_bid: 'booking',
  booking_amount: 'booking',
  booking_enquiry: 'booking',
  agency_booking: 'artists',
  venue: 'venue',
  venue_rate: 'venue',
  venue_enquiry: 'venue',
  venue_night: 'venue',
  venue_registry: 'venues',
  date_request: 'venues',
  concert: 'concerts',
  concert_budget: 'concerts',
  concert_template: 'concerts',
  external: 'sharing',
  share_link: 'sharing',
  share: 'sharing',
  shared_advance: 'sharing',
  /* F13: årgangens kart som noe som deles. */
  map: 'sharing',
  object_grant: 'sharing',
  audit_log: 'activity',
};

/** Området til en modul, for en ressurs uten en egen linje over (G1: gruppert etter modulen). */
const AREA_OF_MODULE: Readonly<Record<OrganizationModule, FixedArea>> = {
  festival: 'festival',
  venue: 'venue',
  venues: 'venues',
  concerts: 'concerts',
  booking: 'booking',
  agency: 'artists',
};

export function fixedAreaOf(resource: string): FixedArea {
  const named = AREA_OF_RESOURCE[resource];
  if (named !== undefined) return named;
  if (!(RESOURCE_TYPES as readonly string[]).includes(resource)) return 'other';
  const modules = modulesOfResource(resource as ResourceType);
  const first = modules === CORE ? undefined : modules[0];
  return first === undefined ? 'other' : AREA_OF_MODULE[first];
}

/**
 * Hvilken seksjon (modul) et område hører til. Brukes for en justerbar funksjon som ikke står i
 * noen innstilling («Flere valg», GRUNDER.md del B): den havner i modulens seksjon, og alt som
 * ikke hører til en modul — organisasjon, kontakter, oppgaver, aktivitet, «Annet» — i Generelt.
 */
const MODULE_OF_AREA: Readonly<Record<FixedArea, PermissionModule>> = {
  organization: 'general',
  contacts: 'general',
  tasks: 'general',
  inbox: 'general',
  documents: 'general',
  calendar: 'general',
  structure: 'general',
  activity: 'general',
  other: 'general',
  festival: 'festival',
  /* Mannskap og vakter har egen seksjon (sak 231.5), som også et rent spillested har. */
  people: 'crew',
  resources: 'festival',
  money: 'money',
  booking: 'booking',
  artists: 'artists',
  venue: 'venue',
  venues: 'venues',
  concerts: 'concerts',
  sharing: 'sharing',
};

const SECTION_OF_ACTION: ReadonlyMap<string, PermissionModule> = new Map(
  PERMISSION_SECTIONS.flatMap((section) =>
    section.settings.flatMap((setting) =>
      setting.actions.map((action) => [action as string, section.id] as const),
    ),
  ),
);

/** Seksjonen en funksjon vises i: innstillingens seksjon, ellers områdets modul, ellers Generelt. */
export function moduleOfAction(action: string): PermissionModule {
  const section = SECTION_OF_ACTION.get(action);
  if (section !== undefined) return section;
  return MODULE_OF_AREA[fixedAreaOf(splitAction(action as Action).resource)];
}

export function fixedAreaTitle(t: TFunction, area: FixedArea): string {
  return t(dynamicKey(`settings.permissions.fixed.area.${area}`));
}

/** «Ressurs og verb» som ord: `thread_participant` → «thread participant» om oversettelsen mangler. */
const humanise = (word: string): string => word.replace(/_/g, ' ');

/**
 * Et lesbart navn på en handling: «Kontakter — se liste».
 *
 * Er handlingen en del av en justerbar innstilling, er det innstillingens navn som gjelder — det er
 * det samme ordet leseren møter øverst på siden, og i aktivitetsloggen.
 */
export function actionLabel(t: TFunction, action: Action | string): string {
  const setting = settingOfAction(action);
  if (setting !== null) return settingLabel(t, setting);
  return plainActionLabel(t, action);
}

/**
 * «Ressurs — verb» uten å gå via innstillingen. For en funksjon som vises ALENE selv om den hører
 * til en innstilling (innstillingen er ikke hel), ellers ville to rader bære det samme navnet.
 */
export function plainActionLabel(t: TFunction, action: Action | string): string {
  const { resource, verb } = splitAction(action as Action);
  return t('settings.permissions.fixed.name', {
    resource: t(dynamicKey(`settings.permissions.fixed.resource.${resource}`), {
      defaultValue: humanise(resource),
    }),
    verb: t(dynamicKey(`settings.permissions.fixed.verb.${verb}`), {
      defaultValue: humanise(verb),
    }),
  });
}

const SETTING_OF_ACTION: ReadonlyMap<string, string> = new Map(
  PERMISSION_SECTIONS.flatMap((section) =>
    section.settings.flatMap((setting) =>
      setting.actions.map((action) => [action as string, setting.id] as const),
    ),
  ),
);

/** Innstillingen en handling hører til, eller `null` for en fast regel. */
export function settingOfAction(action: string): string | null {
  return SETTING_OF_ACTION.get(action) ?? null;
}
